import z$1, { z } from "zod";
import { Implements, Injectable, Service, ServiceLifetime, collection, createCollectionId, createInterfaceId } from "@gitlab/needle";
import { Logger, withPrefix } from "@gitlab-org/logging";
import { isBinaryContent } from "@gitlab-org/fs";
import { SecretRedactor } from "@gitlab-org/secret-redaction";
import { DuoFeatureAccessService } from "@gitlab-org/duo-feature-access";
//#region src/ai_context_category.ts
const AIContextCategory = z$1.enum([
	"file",
	"snippet",
	"terminal",
	"issue",
	"merge_request",
	"dependency",
	"local_git",
	"user_rule",
	"repository",
	"directory",
	"agent_user_environment",
	"os_information",
	"plan_context"
]);
//#endregion
//#region src/context_providers/ai_context_provider.ts
const AIContextProviderTypeSchema = z$1.enum([
	"open_tab",
	"import",
	"local_file_search",
	"issue",
	"merge_request",
	"snippet",
	"dependency",
	"local_git",
	"user_rule",
	"repository",
	"directory",
	"shell",
	"os",
	"mode",
	"hook"
]);
const AIContextProvider = createInterfaceId("AIContextProvider");
var AbstractAIContextProvider = class {
	#selectedContextItems = [];
	/**
	* If set, the provider will be enabled only if the current user has the specified feature enabled.
	* If not set, the provider will always be available
	*/
	constructor(type = "open_tab", logger) {
		this.#selectedContextItems = [];
		this.type = type;
		this.logger = logger;
	}
	notifyContextItemAdded(contextItem) {
		if (this.onContextItemAdded) this.onContextItemAdded(contextItem);
	}
	notifyContextItemToBeRemoved(contextItem) {
		if (this.onBeforeContextItemRemoved) this.onBeforeContextItemRemoved(contextItem);
	}
	notifyAllContextItemsToBeRemoved() {
		if (this.onBeforeAllContextItemsRemoved) this.onBeforeAllContextItemsRemoved();
	}
	async itemAllowedByPolicies(contextItem) {
		if (this.canItemBeAdded) return this.canItemBeAdded(contextItem);
		return Promise.resolve({ enabled: true });
	}
	#findItemWithId(id) {
		return this.#selectedContextItems.find((item) => item.id === id);
	}
	async addSelectedContextItem(contextItem) {
		if (!await this.itemAllowedByPolicies(contextItem)) {
			this.logger.error(`Context item is not allowed by context policies: ${JSON.stringify(contextItem)}`);
			return;
		}
		if (contextItem.metadata.subType !== this.type) this.logger.error(`Context item type "${contextItem.metadata.subType}" does not match context provider type "${this.type}"`);
		else if (this.#findItemWithId(contextItem.id)) this.logger.error(`Context item with ID "${contextItem.id}" already exists`);
		else {
			this.#selectedContextItems.push(contextItem);
			this.notifyContextItemAdded(contextItem);
		}
	}
	async removeSelectedContextItem(id) {
		const item = this.#selectedContextItems.find((i) => i.id === id);
		if (!item) {
			const error = /* @__PURE__ */ new Error(`Item with id ${id} not found in context items.`);
			this.logger.error(error);
			throw error;
		}
		this.notifyContextItemToBeRemoved(item);
		const index = this.#selectedContextItems.indexOf(item);
		this.#selectedContextItems.splice(index, 1);
	}
	async clearSelectedContextItems() {
		if (this.#selectedContextItems.length) {
			this.notifyAllContextItemsToBeRemoved();
			this.#selectedContextItems = [];
		}
	}
	async getSelectedContextItems() {
		return this.#selectedContextItems;
	}
};
//#endregion
//#region src/ai_context_item.ts
const AIContextItemMetadataSchema = z$1.object({
	title: z$1.string(),
	enabled: z$1.boolean(),
	disabledReasons: z$1.array(z$1.string()).optional(),
	subType: AIContextProviderTypeSchema,
	icon: z$1.string(),
	secondaryText: z$1.string(),
	subTypeLabel: z$1.string(),
	/**
	* The languageId of the file, if the context item is a file
	*/
	languageId: z$1.string().optional()
});
const AIContextItemBaseSchema = z$1.object({
	id: z$1.string(),
	category: AIContextCategory,
	content: z$1.string().optional(),
	metadata: AIContextItemMetadataSchema.optional()
});
const AIContextItemSchema = AIContextItemBaseSchema.extend({ metadata: AIContextItemMetadataSchema });
/**
* Items that can be added to the AI context
* @property {string} id - The id of the context item
* @property {string} [content] - The content of the context item to be used by the AI, only sent on retrieval
* @property {AIContextCategory} category - The category or source of the context item
* @property {AIContextItemMetadata} metadata - Metadata about the context item for UI, format depends on category
*/
//#endregion
//#region src/chat_context_manager.ts
const DuoChatContextManager = createInterfaceId("DuoChatContextManager");
const AgenticChatContextManager = createInterfaceId("AgenticChatContextManager");
const AIContextMapping = {
	file: ["open_tab", "local_file_search"],
	snippet: ["snippet"],
	terminal: ["snippet"],
	issue: ["issue"],
	merge_request: ["merge_request"],
	dependency: ["dependency"],
	local_git: ["local_git"],
	repository: ["repository"],
	user_rule: ["user_rule"],
	directory: ["directory"],
	agent_user_environment: ["shell"],
	os_information: ["os"],
	plan_context: []
};
var DefaultChatContextManager = class {
	#providers = [];
	#transformerService;
	#duoFeatureAccessService;
	#workspaceFolders;
	#logger;
	constructor(logger, configService, aiContextTransformerService, providers, duoFeatureAccessService) {
		this.#logger = withPrefix(logger, "ChatContextManager");
		this.#transformerService = aiContextTransformerService;
		this.#providers = providers;
		this.#duoFeatureAccessService = duoFeatureAccessService;
		this.#workspaceFolders = configService.get("workspaceFolders") ?? [];
		configService.onConfigChange((config) => {
			this.#workspaceFolders = config.workspaceFolders ?? [];
		});
	}
	async #getAvailableProviders() {
		const providerPromises = this.#providers.map(async (provider) => {
			return await this.#duoFeatureAccessService.isChatFeatureEnabled(provider.chatRequiredFeature) ? provider : void 0;
		});
		return (await Promise.all(providerPromises)).filter((p) => p !== void 0);
	}
	async #getProviderAndPerform(subType, callback) {
		const e = /* @__PURE__ */ new Error("No provider found for type");
		const provider = (await this.#getAvailableProviders()).find((p) => p.type === subType);
		if (!provider) throw e;
		await callback(provider);
	}
	async #getProviderForItem(item) {
		const foundProvider = (await this.#getAvailableProviders()).find((provider) => provider.type === item.metadata?.subType);
		if (!foundProvider) throw new Error(`No provider found for type "${item.metadata.subType || "undefined item subtype"}"`);
		return foundProvider;
	}
	async addSelectedContextItem(contextItem) {
		try {
			this.#logger.info(`received context item add request ${contextItem.id}`);
			await this.#getProviderAndPerform(contextItem.metadata?.subType, async (provider) => {
				await provider.addSelectedContextItem(contextItem);
			});
			this.#logger.info(`added item result true`);
			return true;
		} catch (e) {
			this.#logger.error(`error adding context item`, e);
			return false;
		}
	}
	async removeSelectedContextItem(contextItem) {
		this.#logger.info(`received context item remove request ${contextItem.id}`);
		try {
			await this.#getProviderAndPerform(contextItem.metadata?.subType, async (provider) => {
				await provider.removeSelectedContextItem(contextItem.id);
			});
			this.#logger.info(`removed item result true`);
			return true;
		} catch (e) {
			this.#logger.error(`error removing context item`, e);
			return false;
		}
	}
	async getSelectedContextItems() {
		const availableProviders = await this.#getAvailableProviders();
		const currentItems = (await Promise.all(availableProviders.map((provider) => provider.getSelectedContextItems()))).flat();
		this.#logger.info(`returning ${currentItems.length} current context items`);
		return currentItems;
	}
	async searchContextItems(request) {
		this.#logger.info(`received context search query, category: ${request.category}, query: ${request.query}`);
		try {
			const contextItems = [];
			const typesForCategory = AIContextMapping[request.category];
			const workspaceFolders = request.workspaceFolders ?? this.#workspaceFolders;
			if (!workspaceFolders.length) this.#logger.debug(`No workspace folders detected.`);
			const providerQuery = {
				...request,
				workspaceFolders
			};
			const contextItemsPromises = (await this.#getAvailableProviders()).filter((p) => typesForCategory.includes(p.type)).map(async (provider) => {
				return provider.searchContextItems(providerQuery);
			});
			(await Promise.all(contextItemsPromises)).forEach((items) => contextItems.push(...items));
			const selectedContextItemsSet = new Set((await this.getSelectedContextItems()).map((item) => item?.id).filter((id) => id !== void 0));
			const nonSelectedItems = contextItems.filter((item) => !selectedContextItemsSet.has(item.id));
			const results = this.#disabledContextItemsToTheBack(nonSelectedItems);
			this.#logger.info(`context search query had ${results.length} results`);
			return results;
		} catch (e) {
			this.#logger.error(`error searching context items`, e);
			return [];
		}
	}
	#disabledContextItemsToTheBack(contextItems) {
		return contextItems.sort((a, b) => {
			if (a.metadata?.enabled === b.metadata?.enabled) return 0;
			return a.metadata?.enabled ? -1 : 1;
		});
	}
	async getAvailableCategories() {
		const types = (await this.#getAvailableProviders()).map((provider) => provider.type);
		const categories = Object.keys(AIContextMapping);
		const availableCategories = types.map((type) => categories.find((category) => AIContextMapping[category].includes(type))).filter((category) => category !== void 0);
		this.#logger.info(`returning ${availableCategories.length} available context provider categories: "${availableCategories.join(", ")}"`);
		return availableCategories;
	}
	/**
	* Retrieves context items with content for the given AI request.
	*
	* @deprecated - TODO: We are going to redesign the overall architecture to be modular at the feature level,
	* see https://gitlab.com/gitlab-org/editor-extensions/gitlab-lsp/-/issues/752#note_2310700284
	*
	*/
	async retrieveContextItemsWithContent(request) {
		const availableProviders = await this.#getAvailableProviders();
		const contextItems = (await Promise.all(availableProviders.map(async (provider) => {
			try {
				return await provider.retrieveContextItemsWithContent(request);
			} catch (error) {
				this.#logger.error(`Error retrieving context items from provider type: ${provider.type}`, error);
				return [];
			}
		}))).flat().filter(Boolean);
		return Promise.all(contextItems.map((contextItem) => this.#transformItemContent(contextItem)));
	}
	async clearSelectedContextItems() {
		this.#logger.info("clearing all selected context items");
		(await this.#getAvailableProviders()).forEach((provider) => provider.clearSelectedContextItems());
		return true;
	}
	async getItemWithContent(item) {
		this.#logger.info(`received get context item content request ${item.id}`);
		const itemWithContent = await (await this.#getProviderForItem(item)).getItemWithContent(item);
		this.#logger.info(`retrieved context item content ${itemWithContent}`);
		return this.#transformItemContent(itemWithContent);
	}
	async #transformItemContent(item) {
		try {
			return await this.#transformerService.transform(item);
		} catch (error) {
			this.#logger.error(`Error transforming context item content. Item content will be excluded.`, error);
			return {
				...item,
				content: void 0
			};
		}
	}
	async getProviderForType(type) {
		const provider = this.#providers.find((p) => p.type === type);
		if (!provider) throw new Error(`No provider found for type "${type}"`);
		return provider;
	}
};
//#endregion
//#region src/context_transformers/ai_context_transformer.ts
const AiContextTransformer = createInterfaceId("AiContextTransformer");
//#endregion
//#region src/context_transformers/ai_context_binary_file_transformer.ts
let _initClass$3;
function _applyDecs$3(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$3(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$3(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$3(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$3(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$3(t) {
	var i = _toPrimitive$3(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$3(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$3(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$3(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const BINARY_FILE_DISABLED_REASON = "Binary files are unsupported";
let _DefaultBinaryFileTra;
(class {
	static {
		[_DefaultBinaryFileTra, _initClass$3] = _applyDecs$3(this, [Injectable(AiContextTransformer, [Logger])], []).c;
	}
	#logger;
	constructor(logger) {
		this.#logger = withPrefix(logger, "[BinaryFileTransformer]");
	}
	async transform(context) {
		if (!context.content) return context;
		if (isBinaryContent(context.content)) {
			this.#logger.debug(`Detected binary content in "${context.id}". File disabled.`);
			return {
				...context,
				content: "",
				metadata: {
					...context.metadata,
					enabled: false,
					disabledReasons: [...context.metadata.disabledReasons || [], BINARY_FILE_DISABLED_REASON]
				}
			};
		}
		return context;
	}
	static {
		_initClass$3();
	}
});
//#endregion
//#region src/context_transformers/ai_context_secret_transformer.ts
let _initClass$2;
function _applyDecs$2(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$2(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$2(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$2(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$2(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$2(t) {
	var i = _toPrimitive$2(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$2(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$2(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$2(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
let _DefaultSecretContext;
(class {
	static {
		[_DefaultSecretContext, _initClass$2] = _applyDecs$2(this, [Injectable(AiContextTransformer, [Logger, SecretRedactor])], []).c;
	}
	#logger;
	#secretRedactor;
	constructor(logger, secretRedactor) {
		this.#secretRedactor = secretRedactor;
		this.#logger = withPrefix(logger, "[SecretContextTransformer]");
	}
	async transform(context) {
		if (!context.content) return context;
		this.#logger.debug(`Transforming context item "${context.id}"`);
		const redactedContent = this.#secretRedactor.redactSecrets(context.content, context.id);
		return {
			...context,
			content: redactedContent
		};
	}
	static {
		_initClass$2();
	}
});
//#endregion
//#region src/context_transformers/ai_context_transformer_service.ts
let _initClass$1;
function _applyDecs$1(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$1(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$1(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$1(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$1(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$1(t) {
	var i = _toPrimitive$1(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$1(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$1(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$1(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const AiContextTransformerService = createInterfaceId("AiContextTransformerService");
let _DefaultAiContextTran;
(class {
	static {
		[_DefaultAiContextTran, _initClass$1] = _applyDecs$1(this, [Injectable(AiContextTransformerService, [collection(AiContextTransformer)])], []).c;
	}
	#transformers;
	constructor(transformers) {
		this.#transformers = transformers;
	}
	async transform(contextItem) {
		let transformedItem = contextItem;
		for (const transformer of this.#transformers) transformedItem = await transformer.transform(transformedItem);
		return transformedItem;
	}
	static {
		_initClass$1();
	}
});
//#endregion
//#region src/system_context/system_context_provider.ts
const SystemContextProvider = createInterfaceId("SystemContextProvider");
//#endregion
//#region src/system_context/system_context_manager.ts
let _initClass;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const SystemContextManager = createInterfaceId("SystemContextManager");
const SystemContextProviderCollection = createCollectionId(SystemContextProvider);
let _DefaultSystemContext;
(class {
	static {
		[_DefaultSystemContext, _initClass] = _applyDecs(this, [Service({
			dependencies: [
				Logger,
				collection(SystemContextProvider),
				DuoFeatureAccessService,
				SecretRedactor
			],
			lifetime: ServiceLifetime.Singleton
		}), Implements(SystemContextManager)], []).c;
	}
	#providers = [];
	#duoFeatureAccessService;
	#secretRedactor;
	#logger;
	constructor(logger, providers, duoFeatureAccessService, secretRedactor) {
		this.#logger = withPrefix(logger, "[SystemContextManager]");
		this.#providers = providers;
		this.#duoFeatureAccessService = duoFeatureAccessService;
		this.#secretRedactor = secretRedactor;
	}
	async #getAvailableProviders() {
		const providerPromises = this.#providers.map(async (provider) => {
			return await this.#isEnabled(provider) ? provider : null;
		});
		return (await Promise.all(providerPromises)).filter(Boolean);
	}
	async #isEnabled(systemContextProvider) {
		try {
			let enabled = true;
			if (systemContextProvider.chatRequiredFeature) {
				enabled = await this.#duoFeatureAccessService.isChatFeatureEnabled(systemContextProvider.chatRequiredFeature);
				this.#logger.debug(`System provider with required feature "${systemContextProvider.chatRequiredFeature}": ${enabled}`);
			} else this.#logger.debug(`System provider has no feature requirement, enabled by default`);
			return enabled;
		} catch (e) {
			this.#logger.warn(`Failed to get enabled status for system provider with required feature ${systemContextProvider.chatRequiredFeature} treating it as disabled.`, e);
			return false;
		}
	}
	async precalculateOnInitialized() {
		await this.#runPrecalculate(this.#providers);
	}
	async precalculateOnWorkflowStart() {
		const providers = (await this.#getAvailableProviders()).filter((p) => typeof p.precalculateOnWorkflowStart === "function");
		this.#logger.info(`Precalculating system context for ${providers.length} providers on workflow start`);
		const precalculatePromises = providers.map(async (provider) => {
			if (typeof provider.precalculateOnWorkflowStart === "function") {
				const providerName = provider.constructor.name;
				const startMs = Date.now();
				try {
					await provider.precalculateOnWorkflowStart();
					this.#logger.info(`[precalculateOnWorkflowStart] ${providerName} took ${Date.now() - startMs}ms`);
				} catch (error) {
					this.#logger.warn(`Error precalculating for system context provider`, error);
				}
			}
		});
		await Promise.all(precalculatePromises);
		this.#logger.info(`System context precalculation complete for workflow start`);
	}
	async #runPrecalculate(providers) {
		const providerNames = providers.filter((p) => "precalculate" in p && typeof p.precalculate === "function").map((p) => p.constructor.name);
		this.#logger.info(`[precalculateOnInitialized] running for ${providerNames.length} providers: ${providerNames.join(", ")}`);
		const precalculatePromises = providers.map(async (provider) => {
			if ("precalculate" in provider && typeof provider.precalculate === "function") {
				const providerName = provider.constructor.name;
				const startMs = Date.now();
				try {
					await provider.precalculate();
					this.#logger.info(`[precalculateOnInitialized] ${providerName} took ${Date.now() - startMs}ms`);
				} catch (error) {
					this.#logger.warn(`Error precalculating for system context provider`, error);
				}
			}
		});
		await Promise.all(precalculatePromises);
		this.#logger.info(`System context precalculation complete for initialized`);
	}
	async getSystemContextItems(context) {
		const availableProviders = await this.#getAvailableProviders();
		this.#logger.info(`[getSystemContextItems] running for ${availableProviders.length} providers: ${availableProviders.map((p) => p.constructor.name).join(", ")}`);
		const providerPromises = availableProviders.map(async (provider) => {
			const providerName = provider.constructor.name;
			const startMs = Date.now();
			try {
				const items = await provider.getItems(context);
				this.#logger.info(`[getSystemContextItems] ${providerName} took ${Date.now() - startMs}ms`);
				return items;
			} catch (error) {
				this.#logger.error(`Error getting items from system context provider`, error);
				return [];
			}
		});
		const redactedItems = (await Promise.all(providerPromises)).flat().map((item) => this.#redactItem(item));
		this.#logger.info(`Retrieved ${redactedItems.length} system context items`);
		return redactedItems;
	}
	#redactItem(item) {
		if (!item.content) return item;
		try {
			return {
				...item,
				content: this.#secretRedactor.redactSecrets(item.content, item.id)
			};
		} catch (error) {
			this.#logger.error(`Error redacting system context item "${item.id}". Item content will be excluded.`, error);
			return {
				...item,
				content: void 0
			};
		}
	}
	static {
		_initClass();
	}
});
//#endregion
//#region src/index.ts
const DuoChatAIRequest = z.object({
	category: AIContextCategory,
	query: z.string(),
	workspaceFolders: z.array(z.object({
		uri: z.string(),
		name: z.string()
	})).optional()
});
//#endregion
export { AIContextCategory, AIContextItemBaseSchema, AIContextItemMetadataSchema, AIContextItemSchema, AIContextProvider, AIContextProviderTypeSchema, AbstractAIContextProvider, AgenticChatContextManager, AiContextTransformer, AiContextTransformerService, BINARY_FILE_DISABLED_REASON, _DefaultAiContextTran as DefaultAiContextTransformerService, _DefaultBinaryFileTra as DefaultBinaryFileTransformer, DefaultChatContextManager, _DefaultSecretContext as DefaultSecretContextTransformer, _DefaultSystemContext as DefaultSystemContextManager, DuoChatAIRequest, DuoChatContextManager, SystemContextManager, SystemContextProvider, SystemContextProviderCollection };

//# sourceMappingURL=index.mjs.map